import React from "react"

import { WithLoadingIndicator } from "~tailwindui/Basics"

import { gql, useClient, useQuery } from "hooks/urql"

import LocalTime from "components/LocalTime"
import ModalWithProvidedBody from "components/ModalWithProvidedBody"

import MicrophoneSelect from "./MicrophoneSelect"

type JoinModalProps = {
  audioRoomId: string
  audioInputDevices: MediaDeviceInfo[]
  selectedAudioInputDeviceId: string
  setSelectedAudioInputDeviceId: (deviceId: string) => void
  microphoneError?: string
  onJoin: () => void
  joining: boolean
  hideMicSelect?: boolean
}

const JoinModal: React.FC<JoinModalProps> = ({
  audioRoomId,
  audioInputDevices,
  selectedAudioInputDeviceId,
  setSelectedAudioInputDeviceId,
  microphoneError,
  onJoin,
  joining,
  hideMicSelect = false,
}) => {
  const client = useClient()
  const [joinModalDetailsQueryResult] = useQuery({
    query: joinModalDetailsQuery,
    client,
    variables: { audioRoomId },
  })
  return (
    <WithLoadingIndicator result={joinModalDetailsQueryResult}>
      {({ data: { audioRoom } }) => (
        <ModalWithProvidedBody
          isOpen
          hideTrigger
          hideCloseButton
          modalTitle={`Join ${
            audioRoom.activeAcrClassPeriod
              ? "Class"
              : `${audioRoom.teacher.fullName}'s Office`
          }`}
          modalClassName="bootstrap-modal"
          style={{ overlay: { backgroundColor: "#111" } }}
        >
          <div className="modal-body">
            {audioRoom.activeAcrClassPeriod && (
              <>
                <p>{audioRoom.activeAcrClassPeriod.acrClass.name}</p>
                <p>
                  Subject:{" "}
                  {audioRoom.activeAcrClassPeriod.acrClass.subject.name}
                </p>
                <p>
                  Teacher:{" "}
                  {audioRoom.activeAcrClassPeriod.acrClass.teacher.fullName}
                </p>
                <p>
                  <LocalTime
                    omitDate
                    timestamp={audioRoom.activeAcrClassPeriod.startsAt}
                  />
                  &nbsp;&mdash;&nbsp;
                  <LocalTime
                    omitDate
                    timestamp={audioRoom.activeAcrClassPeriod.endsAt}
                  />
                </p>
              </>
            )}
            {!hideMicSelect && (
              <>
                <label className="mt-4 block" htmlFor="audio-input-device">
                  Microphone
                </label>
                <MicrophoneSelect
                  audioInputDevices={audioInputDevices}
                  selectedAudioInputDeviceId={selectedAudioInputDeviceId}
                  setSelectedAudioInputDeviceId={setSelectedAudioInputDeviceId}
                  disabled={joining}
                  menuPortalTarget={document.body}
                />
              </>
            )}
            {microphoneError && (
              <p className="mt-2 text-red-600" role="alert">
                {microphoneError}
              </p>
            )}
          </div>

          <div className="modal-footer flex justify-center">
            <button
              type="button"
              className="btn solid green btn-success w-32"
              onClick={onJoin}
              disabled={Boolean(microphoneError) || joining}
            >
              {joining ? "Joining..." : "Join"}
            </button>
          </div>
        </ModalWithProvidedBody>
      )}
    </WithLoadingIndicator>
  )
}

const joinModalDetailsQuery = gql`
  query joinModalDetailsQuery($audioRoomId: ID!) {
    audioRoom(id: $audioRoomId) {
      teacher {
        fullName
      }
      activeAcrClassPeriod {
        startsAt
        endsAt
        acrClass {
          name
          subject {
            name
          }
          teacher {
            fullName
          }
        }
      }
    }
  }
`

export default JoinModal
